import React from 'react';
import { Box, Card, CardContent, Chip, Divider, Tooltip, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  ArrowBack as ArrowBackIcon,
  ArrowForward as ArrowForwardIcon,
  ShoppingCart as CartIcon,
  Storefront as StorefrontIcon,
  Inventory2 as Inventory2Icon,
  Paid as PaidIcon,
  PersonOff as PersonOffIcon,
  PersonOutline as PersonOutlineIcon,
} from '@mui/icons-material';
import { formatCurrency } from '../../../utils/formatters/money';
import { BackButton, ConfirmButton } from '../SalesStep3/SalesStep3.styles';
import { getSaleInvoiceLabel } from '../../../utils/sales/sale-invoice.utils';
import { cartHasRemoteItems } from '../../../utils/sales/mixed-sale.utils';
import type { CustomerProfile, Location, SelectedProduct } from '../../../types/sales.types';

export interface SaleStep3SummaryCardProps {
  selectedLocation: Location | null;
  selectedProducts: SelectedProduct[];
  selectedCustomer: CustomerProfile | null;
  hasInvoice: boolean;
  grandTotal: number;
  isLoading: boolean;
  onPreviousStep: () => void;
  onContinueStep4: () => void;
  onContinueWithoutCustomer: () => void;
  disablePreviousStep?: boolean;
}

const SaleStep3SummaryCard: React.FC<SaleStep3SummaryCardProps> = ({
  selectedLocation,
  selectedProducts,
  selectedCustomer,
  hasInvoice,
  grandTotal,
  isLoading,
  onPreviousStep,
  onContinueStep4,
  onContinueWithoutCustomer,
  disablePreviousStep = false,
}) => {
  return (
    <Card
      elevation={0}
      sx={(theme) => ({
        mt: { xs: 0.75, md: 1 },
        borderRadius: 3,
        border: `1px solid ${alpha(theme.palette.success.main, theme.palette.mode === 'dark' ? 0.35 : 0.25)}`,
        backgroundColor: theme.palette.background.paper,
        boxShadow:
          theme.palette.mode === 'dark'
            ? '0 14px 30px -24px rgba(0, 0, 0, 0.55)'
            : '0 14px 30px -24px rgba(15, 23, 42, 0.5)',
      })}
    >
      <CardContent sx={{ p: { xs: 2, md: 2.5 } }}>
        <Typography
          variant="h6"
          sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1, color: 'text.primary' }}
        >
          <CartIcon color="primary" />
          Resumen de Venta
        </Typography>
        <Box
          sx={{
            display: 'grid',
            gap: 1.25,
            gridTemplateColumns: '1fr',
            mb: 2,
          }}
        >
          <Box
            sx={(theme) => ({
              p: 1.5,
              borderRadius: 2.2,
              backgroundColor:
                theme.palette.mode === 'dark'
                  ? alpha(theme.palette.common.white, 0.06)
                  : alpha(theme.palette.common.black, 0.03),
              border: `1px solid ${theme.palette.divider}`,
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            })}
          >
            <StorefrontIcon sx={{ color: 'text.secondary', fontSize: 18 }} />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="caption" color="text.secondary">
                Sucursal
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  flexWrap: 'wrap',
                }}
              >
                <Typography variant="body1" sx={{ fontWeight: 700, color: 'text.primary' }}>
                  {selectedLocation?.name ?? 'N/A'}
                </Typography>
                {cartHasRemoteItems(selectedProducts, selectedLocation?.id) && (
                  <Chip
                    label="Venta multi-sucursal"
                    size="small"
                    color="warning"
                    variant="outlined"
                    sx={{ height: 22, fontSize: '0.7rem' }}
                  />
                )}
              </Box>
            </Box>
          </Box>

          <Box
            sx={(theme) => ({
              p: 1.5,
              borderRadius: 2.2,
              backgroundColor:
                theme.palette.mode === 'dark'
                  ? alpha(theme.palette.common.white, 0.06)
                  : alpha(theme.palette.common.black, 0.03),
              border: `1px solid ${theme.palette.divider}`,
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            })}
          >
            <Inventory2Icon sx={{ color: 'text.secondary', fontSize: 18 }} />
            <Box>
              <Typography variant="caption" color="text.secondary">
                Piezas
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 700, color: 'text.primary' }}>
                {selectedProducts.length}
              </Typography>
            </Box>
          </Box>

          <Box
            sx={(theme) => ({
              p: 1.5,
              borderRadius: 2.2,
              backgroundColor: alpha(
                theme.palette.success.main,
                theme.palette.mode === 'dark' ? 0.18 : 0.1,
              ),
              border: `1px solid ${alpha(theme.palette.success.main, theme.palette.mode === 'dark' ? 0.45 : 0.35)}`,
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            })}
          >
            <PaidIcon sx={{ color: 'success.light', fontSize: 18 }} />
            <Box>
              <Typography variant="caption" color="text.secondary">
                Total
              </Typography>
              <Typography variant="h6" sx={{ color: 'success.light', fontWeight: 800 }}>
                {formatCurrency(grandTotal)}
              </Typography>
            </Box>
          </Box>
        </Box>

        <Divider sx={{ my: 2 }} />
        <Box
          sx={(theme) => ({
            mb: 2,
            p: 1.5,
            borderRadius: 2.2,
            border: `1px solid ${theme.palette.divider}`,
            backgroundColor:
              theme.palette.mode === 'dark'
                ? alpha(theme.palette.common.white, 0.06)
                : alpha(theme.palette.common.black, 0.03),
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1,
          })}
        >
          <Typography variant="subtitle2" color="text.secondary">
            Cliente asociado
          </Typography>
          <Chip
            icon={
              selectedCustomer != null ? (
                <PersonOutlineIcon sx={{ fontSize: '16px !important' }} />
              ) : (
                <PersonOffIcon sx={{ fontSize: '16px !important' }} />
              )
            }
            label={selectedCustomer != null ? 'Con cliente' : 'Sin cliente'}
            color={selectedCustomer != null ? 'success' : 'default'}
            size="small"
            variant={selectedCustomer != null ? 'filled' : 'outlined'}
            sx={(theme) => ({
              fontWeight: 600,
              ...(selectedCustomer == null && theme.palette.mode === 'dark'
                ? {
                    borderColor: alpha(theme.palette.common.white, 0.2),
                    color: theme.palette.text.secondary,
                    '& .MuiChip-icon': { color: theme.palette.text.secondary },
                  }
                : {}),
            })}
          />
        </Box>

        <Box
          sx={(theme) => ({
            mb: 2,
            p: 1.5,
            borderRadius: 2.2,
            border: `1px solid ${theme.palette.divider}`,
            backgroundColor:
              theme.palette.mode === 'dark'
                ? alpha(theme.palette.common.white, 0.06)
                : alpha(theme.palette.common.black, 0.03),
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1,
          })}
        >
          <Typography variant="subtitle2" color="text.secondary">
            Factura
          </Typography>
          <Chip
            label={getSaleInvoiceLabel(hasInvoice)}
            color={hasInvoice ? 'info' : 'default'}
            size="small"
            sx={{ fontWeight: 600 }}
          />
        </Box>

        <Box sx={{ display: 'grid', gap: 1.2 }}>
          <BackButton
            fullWidth
            variant="tonal"
            onClick={onPreviousStep}
            disabled={disablePreviousStep}
            startIcon={<ArrowBackIcon />}
            sx={{
              borderRadius: 2,
              mb: 0,
            }}
          >
            Regresar al Paso 2
          </BackButton>

          <ConfirmButton
            fullWidth
            variant="contained"
            onClick={onContinueStep4}
            startIcon={<ArrowForwardIcon />}
            disabled={isLoading || selectedCustomer == null}
            sx={{ borderRadius: 2, py: 1.15, mb: 0 }}
          >
            Continuar al Paso 4
          </ConfirmButton>

          <Tooltip
            title={
              selectedCustomer != null
                ? 'Quita el cliente seleccionado para continuar sin cliente.'
                : isLoading
                  ? 'Espera a que termine la búsqueda.'
                  : ''
            }
            disableHoverListener={selectedCustomer == null && !isLoading}
          >
            <Box sx={{ width: '100%' }}>
              <BackButton
                fullWidth
                variant="outlined"
                onClick={onContinueWithoutCustomer}
                startIcon={<PersonOffIcon />}
                disabled={Boolean(selectedCustomer) || isLoading}
                sx={(theme) => ({
                  borderRadius: 2.2,
                  mb: 0,
                  borderColor: theme.palette.divider,
                  color: theme.palette.text.primary,
                  '&:hover': {
                    borderColor: theme.palette.text.secondary,
                    backgroundColor: theme.palette.action.hover,
                  },
                })}
              >
                Continuar sin cliente
              </BackButton>
            </Box>
          </Tooltip>
        </Box>
      </CardContent>
    </Card>
  );
};

export default SaleStep3SummaryCard;
